import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useApi, errMsg } from "@/lib/api";
import { useMe, can } from "@/context/MeContext";
import { FormField } from "@/components/FormField";
import { PageHeader } from "@/components/PageHeader";
import { PasswordDialog } from "@/components/PasswordDialog";

const useRelations = (fields, api) => {
  const [rel, setRel] = useState({});
  useEffect(() => {
    fields.filter((f) => f.type === "relation" || f.type === "relations").forEach((f) => {
      api("get", `/${f.resource}`).then((items) =>
        setRel((r) => ({ ...r, [f.key]: items.filter(f.where || (() => true)).map((i) => ({ value: i[f.valueKey || "id"], label: i[f.labelKey] })) }))
      ).catch(() => {});
    });
  }, [fields, api]);
  return rel;
};

export default function CrudPage({ config }) {
  const { resource, title, subtitle, singular, writePerm, columns, fields, defaults = {}, searchKeys, addPath, rowLink, editPath, embedded, testKey, noCreate, noDelete, noEdit } = config;
  const tid = testKey || resource;
  const navigate = useNavigate();
  const api = useApi();
  const me = useMe();
  const editable = can(me, writePerm);
  const deletable = can(me, "delete") && !noDelete;
  const [secret, setSecret] = useState(null);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const relations = useRelations(fields, api);

  const load = useCallback(() => api("get", `/${resource}`).then(setItems).catch((e) => toast.error(errMsg(e))), [api, resource]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const base = config.filter ? items.filter(config.filter) : items;
    const s = q.trim().toLowerCase();
    return s ? base.filter((i) => searchKeys.some((k) => String(i[k] || "").toLowerCase().includes(s))) : base;
  }, [items, q, searchKeys, config.filter]);

  const save = async (e) => {
    e.preventDefault();
    const missing = fields.find((f) => f.required && (form[f.key] === "" || form[f.key] == null));
    if (missing) return toast.error(`Le champ « ${missing.label} » est obligatoire.`);
    if (fields.some((f) => f.type === "children") && (form.children || []).some((c) => !c.name || !c.birth_date))
      return toast.error("Renseignez le prénom et la date de naissance de chaque enfant.");
    setSaving(true);
    const body = Object.fromEntries(fields.filter((f) => !(form.id && f.createOnly)).map((f) => [f.key, form[f.key] ?? defaults[f.key]]));
    try {
      if (form.id) await api("put", `/${resource}/${form.id}`, body);
      else {
        const res = await api("post", `/${resource}`, body);
        if (res?.temp_password) setSecret(res);
        if (res?.warning) toast.warning(res.warning);
      }
      toast.success(form.id ? "Modifications enregistrées" : `${singular} ajouté(e)`);
      setForm(null);
      load();
      config.onChange?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      const r = await api("delete", `/${resource}/${toDelete.id}`);
      toast.success(r?.trashed > 1 ? `Élément et ${r.trashed - 1} donnée(s) liée(s) placés dans la corbeille` : "Élément placé dans la corbeille");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-8" data-testid={`${tid}-page`}>
      <PageHeader title={embedded ? null : title} subtitle={embedded ? null : subtitle}>
        {editable && !noCreate && (
          <Button onClick={() => (addPath ? navigate(addPath) : setForm({ ...defaults }))} className="bg-[#C2410C] hover:bg-[#9A3412] text-white" data-testid={`${tid}-add-button`}>
            <Plus className="h-4 w-4 mr-1.5" /> Ajouter
          </Button>
        )}
      </PageHeader>

      <div className="bg-white border border-stone-200 rounded-lg">
        <div className="p-4 border-b border-stone-200 flex items-center gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-9" data-testid={`${tid}-search-input`} />
          </div>
          <span className="ml-auto text-xs text-stone-500" data-testid={`${tid}-count`}>{filtered.length} élément(s)</span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((c) => <TableHead key={c.key} className="text-xs uppercase tracking-[0.1em] text-stone-500">{c.label}</TableHead>)}
                {(editable || deletable) && <TableHead className="w-24" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items === null && <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10 text-stone-500">Chargement…</TableCell></TableRow>}
              {items && filtered.length === 0 && <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10 text-stone-500" data-testid={`${tid}-empty`}>Aucun élément</TableCell></TableRow>}
              {filtered.map((it) => (
                <TableRow key={it.id} className={`hover:bg-stone-50 ${rowLink ? "cursor-pointer" : ""}`} onClick={rowLink ? () => navigate(rowLink(it)) : undefined} data-testid={`${tid}-row-${it.id}`}>
                  {columns.map((c) => <TableCell key={c.key} className="text-sm text-stone-700">{c.render ? c.render(it) : it[c.key] || "—"}</TableCell>)}
                  {(editable || deletable) && (
                    <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {editable && !noEdit && <button className="p-2 text-stone-500 hover:text-[#C2410C] transition-colors" onClick={() => (editPath ? navigate(editPath(it)) : setForm({ ...it }))} data-testid={`${tid}-edit-${it.id}`} aria-label="Modifier"><Pencil className="h-4 w-4" /></button>}
                      {deletable && <button className="p-2 text-stone-500 hover:text-red-700 transition-colors" onClick={() => setToDelete(it)} data-testid={`${tid}-delete-${it.id}`} aria-label="Supprimer"><Trash2 className="h-4 w-4" /></button>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${tid}-form-dialog`}>
          <DialogHeader><DialogTitle className="font-heading">{form?.id ? `Modifier` : `Nouveau : ${singular}`}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
              {fields.filter((f) => !(form.id && f.createOnly)).map((f) => (
                <FormField key={f.key} field={f} value={form[f.key]} options={relations[f.key]} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} />
              ))}
              <DialogFooter className="sm:col-span-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setForm(null)} data-testid={`${tid}-form-cancel`}>Annuler</Button>
                <Button type="submit" disabled={saving} className="bg-[#C2410C] hover:bg-[#9A3412] text-white" data-testid={`${tid}-form-submit`}>{saving ? "Enregistrement…" : "Enregistrer"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid={`${tid}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet élément ?</AlertDialogTitle>
            <AlertDialogDescription>Il sera retiré de l'application Sehatic-Pedia et placé dans la corbeille, d'où il pourra être restauré.{resource === "sp/patients" ? " Ses rendez-vous, vaccins et dossiers médicaux suivront." : ""}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${tid}-delete-cancel`}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-700 hover:bg-red-800" data-testid={`${tid}-delete-confirm`}>Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PasswordDialog data={secret} onClose={() => setSecret(null)} />
    </div>
  );
}
